import { Typography } from '@mui/material';
import { Button } from 'components/Ui-V3/Button/Button';

type AccountSwitchConfirmProps = {
    fromEmail: string;
    toEmail: string;
    authenticating: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Shown instead of `MagicLinkConfirm` when a magic-link / invite token has already been
 * exchanged for a *different* account than the one currently logged in — the exchange itself
 * already happened (single-use token), but the cookie/user-store swap is held back until the
 * user explicitly picks a side here.
 */
export const AccountSwitchConfirm = ({
    fromEmail,
    toEmail,
    authenticating,
    onConfirm,
    onCancel,
}: AccountSwitchConfirmProps) => (
    <>
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 500 }}>
            {authenticating ? 'Authenticating...' : 'Switch accounts?'}
        </Typography>
        {!authenticating && (
            <Typography variant="body1" sx={{ mb: 2 }}>
                {`You're signed in as ${fromEmail}. This link signs in as ${toEmail}.`}
            </Typography>
        )}
        <Button
            fullWidth
            autoFocus
            variant="primary"
            shape="square"
            onClick={onConfirm}
            disabled={authenticating}
            aria-label="Continue"
            sx={{ mt: 2 }}
        >
            {authenticating ? 'Authenticating...' : 'Continue'}
        </Button>
        {!authenticating && (
            <>
                <Button
                    fullWidth
                    variant="outline"
                    shape="square"
                    onClick={onCancel}
                    aria-label={`Stay signed in as ${fromEmail}`}
                    sx={{ mt: 1 }}
                >
                    {`Stay signed in as ${fromEmail}`}
                </Button>
                <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    {`This sign-in link will be used up either way — you'll need a new one to sign in as ${toEmail} later.`}
                </Typography>
            </>
        )}
    </>
);
